/**
 * CreateDocumentCtaCard
 * 
 * Call-to-action card for creating documents from drafts suggested by the agent.
 * Navigates the user to the creation page with pre-filled data.
 */
import { useNavigate } from 'react-router-dom';
import type { CreateDocumentCtaCardData } from '../types';

interface CreateDocumentCtaCardProps {
  data: CreateDocumentCtaCardData;
  onSelect: (selection: Record<string, unknown>) => void;
}

export function CreateDocumentCtaCard({ data, onSelect }: CreateDocumentCtaCardProps) {
  const navigate = useNavigate();

  const openDraft = () => {
    navigate('/create-document', { state: { assistantPrefill: data.draft } });
    onSelect({ action: 'open_create_document', destination: '/create-document', draft: data.draft });
  };

  return (
    <div className="my-2 overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
      <div className="border-b border-blue-100 bg-gradient-to-r from-blue-600 to-sky-500 px-4 py-3 text-white">
        <p className="text-xs uppercase tracking-[0.18em] text-blue-100">Draft Ready</p>
        <h3 className="mt-1 text-sm font-semibold">{data.title}</h3>
        {data.description && <p className="mt-1 text-xs text-blue-50/90">{data.description}</p>}
      </div>

      <div className="space-y-2 px-4 py-4 text-sm text-slate-700">
        <div className="rounded-xl bg-slate-50 px-3 py-2">
          <span className="text-xs uppercase tracking-wide text-slate-500">Document</span>
          <p className="mt-1 font-medium capitalize">{data.draft.category?.replace('_', ' ') || 'Invoice'}</p>
        </div>
        {data.draft.customer && (
          <div className="rounded-xl bg-slate-50 px-3 py-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">Customer</span>
            <p className="mt-1 font-medium">{data.draft.customer.display_name}</p>
          </div>
        )}
        {(data.draft.items?.length ?? 0) > 0 && (
          <div className="rounded-xl bg-slate-50 px-3 py-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">Items</span>
            <p className="mt-1 font-medium">{data.draft.items?.length} line items prefilled</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 border-t border-slate-100 px-4 py-3">
        <button
          type="button"
          onClick={openDraft}
          className="flex-1 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700"
        >
          {data.primary_label || 'Open draft'}
        </button>
        <button
          type="button"
          onClick={() => onSelect({ action: 'dismiss' })}
          className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
        >
          {data.secondary_label || 'Later'}
        </button>
      </div>
    </div>
  );
}
